// SPDX-License-Identifier: GPL-3.0-or-later
// The patches of the sound effects. One patch builds the voice for one feedback kind. A patch only
// calls `tone` and `noise` on the voice. It has no state. All sounds are synthesised.
//
// `p.f(hz)` scales a frequency by the pitch of the hit: a harder hit sounds a little higher.

import type { FeedbackEvent, FeedbackKind } from "../../src/shared/feedback/index.ts";
import type { Voice } from "./sfx-voice.ts";

/** What a patch reads. */
export interface PatchInput {
  /** Strength of the hit, 0 to 1. */
  readonly strength: number;
  /** Pitch factor from the strength. Near 1. */
  readonly pitch: number;
  /** The event. */
  readonly event: FeedbackEvent;
  /** Scale a frequency by the pitch. */
  f(hz: number): number;
}

/** A patch. */
export type Patch = (v: Voice, p: PatchInput) => void;

const PENTATONIC = [0, 2, 4, 7, 9];

/** A note: `n` semitones from `hz`. */
export function semitone(hz: number, n: number): number {
  return hz * 2 ** (n / 12);
}

/** A small hash of a name. The same name always gives the same number. */
export function nameHash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h;
}

/** The pitch of a bumper: each bumper has its own note from one scale. */
export function bumperPitch(element: string | undefined): number {
  const h = nameHash(element ?? "");
  const degree = PENTATONIC[h % PENTATONIC.length] ?? 0;
  return semitone(330, degree + 12 * (Math.floor(h / 5) % 2));
}

/** Play notes one after the other. */
function arpeggio(
  v: Voice,
  type: OscillatorType,
  notes: readonly number[],
  step: number,
  dur: number,
  gain: number,
): void {
  notes.forEach((hz, i) => {
    v.tone({ type, from: hz, at: i * step, dur, gain });
  });
}

function whoosh(v: Voice, p: PatchInput, dur: number): void {
  v.noise({
    filter: "bandpass",
    from: p.f(400),
    to: p.f(2600),
    q: 2,
    dur,
    gain: 0.6,
    attack: dur / 3,
  });
  v.tone({ type: "sine", from: p.f(300), to: p.f(900), dur, gain: 0.25, attack: dur / 3 });
}

const flipperUp: Patch = (v, p) => {
  v.noise({ filter: "bandpass", from: p.f(1800), to: 900, q: 1.5, dur: 0.04, gain: 0.6 });
  v.tone({ type: "square", from: p.f(140), to: 70, dur: 0.06, gain: 0.5 });
};

const flipperDown: Patch = (v, p) => {
  v.tone({ type: "triangle", from: p.f(110), to: 60, dur: 0.05, gain: 0.4 });
  v.noise({ filter: "lowpass", from: 900, dur: 0.03, gain: 0.3 });
};

const bumper: Patch = (v, p) => {
  const hz = bumperPitch(p.event.element) * p.pitch;
  v.tone({ type: "square", from: hz * 1.6, to: hz, dur: 0.12, gain: 0.55 });
  v.tone({ type: "sine", from: hz * 2, dur: 0.08, gain: 0.3 });
};

const sling: Patch = (v, p) => {
  v.noise({ filter: "highpass", from: 3000, to: 1200, dur: 0.05, gain: 0.8 });
  v.tone({ type: "sawtooth", from: p.f(220), to: 110, dur: 0.07, gain: 0.45 });
};

const target: Patch = (v, p) => {
  v.tone({ type: "sine", from: p.f(1320), dur: 0.25, gain: 0.6 });
  v.tone({ type: "sine", from: p.f(1980), dur: 0.18, gain: 0.25 });
};

const dropBankComplete: Patch = (v) => {
  arpeggio(v, "triangle", [523, 659, 784, 1047], 0.07, 0.18, 0.55);
};

const spinner: Patch = (v, p) => {
  v.tone({ type: "square", from: p.f(900), dur: 0.02, gain: 0.35 });
  v.noise({ filter: "highpass", from: 4000, dur: 0.015, gain: 0.2 });
};

const lane: Patch = (v, p) => {
  v.tone({ type: "sine", from: p.f(880), dur: 0.1, gain: 0.5 });
};

const kicker: Patch = (v) => {
  v.tone({ type: "sine", from: 90, to: 50, dur: 0.12, gain: 0.7 });
  v.noise({ filter: "lowpass", from: 500, dur: 0.06, gain: 0.4 });
};

const plungerLaunch: Patch = (v, p) => {
  v.noise({ filter: "bandpass", from: p.f(1200), to: 300, q: 2, dur: 0.15, gain: 0.5 });
  v.tone({ type: "triangle", from: p.f(520), to: 130, dur: 0.18, gain: 0.5 });
};

const ballSave: Patch = (v) => {
  v.tone({ type: "sine", from: 1047, dur: 0.5, gain: 0.5 });
  v.tone({ type: "sine", from: 1568, at: 0.12, dur: 0.5, gain: 0.45 });
};

const ballStart: Patch = (v) => {
  v.tone({ type: "triangle", from: 392, to: 523, dur: 0.15, gain: 0.5 });
  v.noise({ filter: "bandpass", from: 1500, dur: 0.05, gain: 0.2 });
};

const multiballStart: Patch = (v) => {
  v.tone({
    type: "sawtooth",
    from: 110,
    to: 880,
    dur: 1,
    gain: 0.4,
    attack: 0.6,
    lowpass: { from: 400, to: 4000 },
  });
  v.noise({ filter: "highpass", from: 500, to: 5000, dur: 1, gain: 0.35, attack: 0.6 });
  v.tone({ type: "sine", from: 70, to: 40, at: 1, dur: 0.3, gain: 0.8 });
};

const jackpot: Patch = (v, p) => {
  const super_ = p.event.kind === "jackpot" && p.event.superJackpot;
  arpeggio(v, "square", [523, 659, 784, 1047, 1319], 0.08, 0.22, 0.4);
  if (super_) arpeggio(v, "triangle", [1047, 1319, 1568, 2093], 0.08, 0.5, 0.35);
  v.noise({ filter: "highpass", from: 3000, dur: super_ ? 0.6 : 0.3, gain: 0.25 });
};

const modeStart: Patch = (v) => {
  v.tone({ type: "sawtooth", from: 220, to: 440, dur: 0.2, gain: 0.4 });
  v.tone({ type: "square", from: 440, at: 0.1, dur: 0.2, gain: 0.35 });
  v.tone({ type: "square", from: 660, at: 0.18, dur: 0.25, gain: 0.35 });
};

const modeComplete: Patch = (v) => {
  arpeggio(v, "sine", [523, 659, 784], 0.1, 0.5, 0.5);
};

const combo: Patch = (v, p) => {
  v.tone({ type: "square", from: p.f(784), dur: 0.12, gain: 0.4 });
  v.tone({ type: "square", from: p.f(1175), at: 0.08, dur: 0.14, gain: 0.4 });
};

const skillShot: Patch = (v) => {
  arpeggio(v, "triangle", [659, 880, 1319], 0.09, 0.25, 0.55);
};

const ballLock: Patch = (v) => {
  v.noise({ filter: "lowpass", from: 400, dur: 0.1, gain: 0.6 });
  v.tone({ type: "sine", from: 80, to: 50, dur: 0.15, gain: 0.7 });
  v.tone({ type: "triangle", from: 660, at: 0.1, dur: 0.25, gain: 0.4 });
};

const tiltWarning: Patch = (v) => {
  v.tone({ type: "square", from: 100, dur: 0.3, gain: 0.4 });
  v.tone({ type: "square", from: 106, dur: 0.3, gain: 0.4 });
};

const tilt: Patch = (v) => {
  v.tone({ type: "sawtooth", from: 60, to: 45, dur: 0.8, gain: 0.7 });
  v.tone({ type: "square", from: 64, to: 48, dur: 0.8, gain: 0.4 });
};

const drain: Patch = (v) => {
  v.tone({
    type: "sawtooth",
    from: 440,
    to: 110,
    dur: 0.8,
    gain: 0.5,
    lowpass: { from: 2000, to: 250 },
  });
};

const gameOver: Patch = (v) => {
  [523, 440, 349, 262].forEach((hz, i) => {
    v.tone({ type: "triangle", from: hz, at: i * 0.22, dur: i === 3 ? 0.9 : 0.3, gain: 0.55 });
  });
};

const extraBall: Patch = (v) => {
  arpeggio(v, "triangle", [523, 659, 784, 1047, 1319], 0.06, 0.2, 0.5);
  v.noise({ filter: "highpass", from: 5000, at: 0.3, dur: 0.3, gain: 0.2 });
};

const highScore: Patch = (v) => {
  arpeggio(v, "square", [392, 523, 659, 784, 1047], 0.1, 0.3, 0.35);
  arpeggio(v, "sine", [1047, 1319, 1568], 0.1, 0.6, 0.3);
};

const featuredTurnStart: Patch = (v) => {
  arpeggio(v, "sawtooth", [392, 587, 784], 0.1, 0.3, 0.3);
  v.tone({ type: "sine", from: 1175, at: 0.3, dur: 0.6, gain: 0.45 });
};

// A boss hit is a soft tick. A blocked hit uses the duller thud of a flipper.
const bossHit: Patch = (v, p) => {
  if (p.event.kind === "boss_hit" && p.event.blocked) flipperDown(v, p);
  else lane(v, p);
};

// The shield sweeps softly when it comes on. Its release plays nothing.
const bossShield: Patch = (v, p) => {
  if (p.event.kind === "boss_shield" && p.event.on) whoosh(v, p, 0.45);
};

/** The patch of each kind that has a one-shot sound. `plunger_charge` has its own voice. */
export const SFX_PATCHES: Readonly<Partial<Record<FeedbackKind, Patch>>> = {
  flipper_up: flipperUp,
  flipper_down: flipperDown,
  bumper,
  sling,
  target,
  drop_bank_complete: dropBankComplete,
  spinner,
  ramp: (v, p) => whoosh(v, p, 0.45),
  orbit: (v, p) => whoosh(v, p, 0.6),
  lane,
  kicker,
  plunger_launch: plungerLaunch,
  ball_save: ballSave,
  ball_start: ballStart,
  multiball_start: multiballStart,
  jackpot,
  mode_start: modeStart,
  mode_complete: modeComplete,
  combo,
  skill_shot: skillShot,
  ball_lock: ballLock,
  tilt_warning: tiltWarning,
  tilt,
  drain,
  game_over: gameOver,
  extra_ball: extraBall,
  high_score: highScore,
  featured_turn_start: featuredTurnStart,
  // The run kinds reuse the patches above (design section 8). No new patch body.
  stage_start: modeStart,
  stage_clear: modeComplete,
  draft_open: ballStart,
  perk_pick: skillShot,
  run_clear: multiballStart,
  hurry_up_start: ballLock,
  hurry_up_collect: jackpot,
  frenzy_start: combo,
  combo_chain: combo,
  extra_ball_lit: extraBall,
  level_best: highScore,
  // The boss kinds reuse the patches above. The shield sweeps on softly, and is silent off.
  boss_start: modeStart,
  boss_hit: bossHit,
  boss_phase: modeStart,
  boss_telegraph: kicker,
  boss_shield: bossShield,
  boss_defeat: modeComplete,
};
